/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Input Validation and Sanitization Utilities
 * Provides functions to validate and sanitize user inputs to prevent injection attacks
 * and other security vulnerabilities.
 */

/**
 * Sanitizes a string to remove potentially harmful characters
 * and HTML tags that could lead to XSS attacks
 * @param input - The string to sanitize
 * @returns A safe sanitized string
 */
export function sanitizeString(input: string): string {
    if (typeof input !== 'string') {
        return '';
    }

    // Remove any HTML tags
    return input
        .replace(/[<>"'`]/g, (char) => {
            const entityMap: { [key: string]: string } = {
                '<': '&lt;',
                '>': '&gt;',
                '"': '&quot;',
                "'": '&#39;',
                '`': '&#x60;'
            };
            return entityMap[char];
        })
        .trim();
}

/**
 * Validates and sanitizes an email address
 * @param email - The email address to validate
 * @returns An object with validation result and sanitized email
 */
export function validateAndSanitizeEmail(email: string): { isValid: boolean; email: string } {
    const sanitized = sanitizeString(email).toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return {
        isValid: emailRegex.test(sanitized),
        email: sanitized
    };
}

/**
 * Validates and sanitizes a URL to prevent injection attacks
 * @param url - The URL to validate
 * @param allowedProtocols - Array of allowed protocols (default: ['http', 'https'])
 * @returns An object with validation result and sanitized URL
 */
export function validateAndSanitizeUrl(
    url: string,
    allowedProtocols: string[] = ['http', 'https']
): { isValid: boolean; url: string } {
    try {
        const sanitized = sanitizeString(url);

        if (sanitized.startsWith('/')) {
            const baseProtocol =
                typeof window !== 'undefined' && window.location && window.location.protocol
                    ? window.location.protocol.replace(':', '').toLowerCase()
                    : 'http';
            const isValidProtocol = allowedProtocols.includes(baseProtocol);
            return {
                isValid: isValidProtocol,
                url: isValidProtocol ? sanitized : ''
            };
        }

        const urlObj = new URL(sanitized);

        const protocol = urlObj.protocol.replace(':', '').toLowerCase();
        const isValidProtocol = allowedProtocols.includes(protocol);

        return {
            isValid: isValidProtocol,
            url: isValidProtocol ? urlObj.toString() : ''
        };
    } catch {
        return {
            isValid: false,
            url: ''
        };
    }
}

/**
 * Validates and sanitizes numeric input
 * @param input - The input to validate
 * @param min - Minimum allowed value (optional)
 * @param max - Maximum allowed value (optional)
 * @returns An object with validation result and sanitized number
 */
export function validateAndSanitizeNumber(input: any, min?: number, max?: number): { isValid: boolean; value: number } {
    const num = parseFloat(input);

    if (isNaN(num)) {
        return { isValid: false, value: 0 };
    }

    if (min !== undefined && num < min) {
        return { isValid: false, value: 0 };
    }

    if (max !== undefined && num > max) {
        return { isValid: false, value: 0 };
    }

    return { isValid: true, value: num };
}

/**
 * Validates and sanitizes an integer input
 * @param input - The input to validate
 * @param min - Minimum allowed value (optional)
 * @param max - Maximum allowed value (optional)
 * @returns An object with validation result and sanitized integer
 */
export function validateAndSanitizeInteger(
    input: any,
    min?: number,
    max?: number
): { isValid: boolean; value: number } {
    const result = validateAndSanitizeNumber(input, min, max);

    if (!result.isValid) {
        return result;
    }

    return {
        isValid: true,
        value: Math.floor(result.value)
    };
}

/**
 * Validates and sanitizes account numbers
 * Removes special characters and validates format
 * @param accountNumber - The account number to validate
 * @returns An object with validation result and sanitized account number
 */
export function validateAndSanitizeAccountNumber(accountNumber: string): { isValid: boolean; value: string } {
    const sanitized = sanitizeString(accountNumber);

    // Account numbers typically contain only alphanumeric characters and hyphens
    const isValid = /^[a-zA-Z0-9-]{0,50}$/.test(sanitized);

    return {
        isValid,
        value: isValid ? sanitized : ''
    };
}

/**
 * Sanitizes and validates form data object
 * @param formData - An object containing form fields
 * @param schema - Validation schema defining field types and constraints
 * @returns An object with validation results and sanitized data
 */
export function validateAndSanitizeFormData(
    formData: { [key: string]: any },
    schema: {
        [key: string]: {
            type: 'string' | 'email' | 'number' | 'integer' | 'url' | 'accountNumber';
            required?: boolean;
            min?: number;
            max?: number;
        };
    }
): { isValid: boolean; data: { [key: string]: any }; errors: { [key: string]: string } } {
    const errors: { [key: string]: string } = {};
    const sanitizedData: { [key: string]: any } = {};

    for (const [key, config] of Object.entries(schema)) {
        const value = formData[key];

        // Check required fields
        if (config.required && (value === undefined || value === null || value === '')) {
            errors[key] = `${key} is required`;
            continue;
        }

        if (!config.required && (value === undefined || value === null || value === '')) {
            sanitizedData[key] = '';
            continue;
        }

        // Validate based on type
        switch (config.type) {
            case 'string':
                sanitizedData[key] = sanitizeString(value);
                break;

            case 'email': {
                const result = validateAndSanitizeEmail(value);
                if (!result.isValid) {
                    errors[key] = `${key} is not a valid email`;
                } else {
                    sanitizedData[key] = result.email;
                }
                break;
            }

            case 'number': {
                const result = validateAndSanitizeNumber(value, config.min, config.max);
                if (!result.isValid) {
                    errors[key] = `${key} is not a valid number`;
                } else {
                    sanitizedData[key] = result.value;
                }
                break;
            }

            case 'integer': {
                const result = validateAndSanitizeInteger(value, config.min, config.max);
                if (!result.isValid) {
                    errors[key] = `${key} is not a valid integer`;
                } else {
                    sanitizedData[key] = result.value;
                }
                break;
            }

            case 'url': {
                const result = validateAndSanitizeUrl(value);
                if (!result.isValid) {
                    errors[key] = `${key} is not a valid URL`;
                } else {
                    sanitizedData[key] = result.url;
                }
                break;
            }

            case 'accountNumber': {
                const result = validateAndSanitizeAccountNumber(value);
                if (!result.isValid) {
                    errors[key] = `${key} contains invalid characters`;
                } else {
                    sanitizedData[key] = result.value;
                }
                break;
            }

            default:
                sanitizedData[key] = sanitizeString(value);
        }
    }

    return {
        isValid: Object.keys(errors).length === 0,
        data: sanitizedData,
        errors
    };
}

/**
 * Validates that a value is within allowed options
 * Prevents parameter pollution attacks
 * @param value - The value to validate
 * @param allowedValues - Array of allowed values
 * @returns Whether the value is allowed
 */
export function validateAllowedValue<T>(value: T, allowedValues: T[]): boolean {
    return allowedValues.includes(value);
}

/**
 * Sanitizes object keys to prevent injection attacks via property names
 * @param obj - Object to sanitize
 * @returns New object with sanitized keys
 */
export function sanitizeObjectKeys(obj: { [key: string]: any }): { [key: string]: any } {
    const sanitized: { [key: string]: any } = {};

    for (const [key, value] of Object.entries(obj)) {
        const sanitizedKey = sanitizeString(key);

        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            sanitized[sanitizedKey] = sanitizeObjectKeys(value);
        } else if (Array.isArray(value)) {
            sanitized[sanitizedKey] = value.map((item) =>
                typeof item === 'object' && item !== null ? sanitizeObjectKeys(item) : item
            );
        } else if (typeof value === 'string') {
            sanitized[sanitizedKey] = sanitizeString(value);
        } else {
            sanitized[sanitizedKey] = value;
        }
    }

    return sanitized;
}
